import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '@env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CredentialsService } from './credentials.service';
import { BehaviorSubject } from 'rxjs';
import { User } from './User';
const REGISTRATION_PATH = 'user/register?_format=json';
const LOGIN_PATH = 'user/login?_format=json';
const SUBSCRIBE = 'node/?_format=json';
const X_CSRF_TOKEN = 'session/token';
const FORGOT_PASSWORD = 'user/lost-password?_format=json';
const RESET_PASSWORD = 'user/lost-password-reset?_format=json';
import { map } from 'rxjs/operators';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

export interface LoginContext {
  username: string;
  password: string;
  remember?: boolean;
  token?: string;
}

/**
 * Provides a base for authentication workflow.
 * The login/logout methods should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  userProfile: Observable<any>;
  userProfileSubject: BehaviorSubject<any>;

  constructor(private credentialsService: CredentialsService, private http: HttpClient) {
    this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser')));
    this.currentUser = this.currentUserSubject.asObservable();
  }

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @return The user credentials.
   */
  public get currentUserValue(): any {
    // get current user deatils
    return this.currentUserSubject.value;
  }
  /**
   * Login the user
   * @param context
   */
  login(context: LoginContext): Observable<any> {
    return this.http
      .post<any>(`${environment.BaseURL}/${LOGIN_PATH}`, {
        name: context.username,
        pass: context.password,
      })
      .pipe(
        map((user) => {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage.setItem('access_token', user.access_token);
          localStorage.setItem('csrf_token', user.csrf_token);
          localStorage.setItem('currentUser', JSON.stringify(user));
          this.currentUserSubject.next(user);
          return user;
        })
      );
  }
  /**
   * Register the user
   * @param user
   */
  register(user: any): Observable<any> {
    return this.http.post(
      `${environment.BaseURL}/${REGISTRATION_PATH}`,
      {
        mail: [{ value: user.mail }],
        name: [{ value: user.name }],
        pass: [{ value: user.pass }],
        field_country: user.field_country,
      },
      httpOptions
    );
  }

  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  logout(): Observable<boolean> {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('access_token');
    localStorage.removeItem('csrf_token');
    this.currentUserSubject.next(null);
    // Customize credentials invalidation here
    this.credentialsService.setCredentials();
    return of(true);
  }
  /**
   * get access token from the localstorage
   */
  getToken() {
    return localStorage.getItem('access_token');
  }
  /**
   * get crfs token from the localstorage
   */
  getCRFSToken() {
    return localStorage.getItem('csrf_token');
  }
  /**
   * get the user profile informaion by passing user id and accesstoken(Authorization)
   * @param id
   */
  getUserProfile(id: any): Observable<any> {
    return this.http
      .get(`${environment.BaseURL}/user/${id}?_format=json`, {
        headers: {
          Authorization: 'Bearer ' + this.getToken(),
        },
      })
      .pipe(
        map((res: Response) => {
          return res || {};
        })
      );
  }
  /**
   * get the Account Delete Reasons
   */
  // getAccountDeleteReasons(): Observable<any> {
  //   return this.http.get(`${environment.BaseURL}/user_delete_account_reasons_list_export`).pipe(
  //     map((res: Response) => {
  //       return res || {};
  //     })
  //   );
  // }
  getAccountDeleteReasons(language: string): Observable<any> {
    language = language.toLowerCase();

    if (language === 'pt') {
      language = 'pt-br';
    }

    if (language === 'zh') {
      language = 'zh-hans';
    }

    const url = `${environment.BaseURL}/user_delete_account_reasons_list_export/${language}`;

    return this.http.get(url).pipe(
      map((res: any) => {
        return res || {};
      })
    );
  }
  /**
   * get temporary CRFS token.
   */
  generateCRFSToken() {
    return this.http
      .get<any>(`${environment.BaseURL}/${X_CSRF_TOKEN}`)
      .toPromise()
      .then((result) => {
        return result;
      });
  }
  /**
   * subscribe the user through mail id and aslo by passing CRFS token
   * @param userMailId
   */
  Usersubscribe(userMailId: string) {
    return this.http
      .post(
        `${environment.BaseURL}/${SUBSCRIBE}`,
        {
          type: [{ target_id: 'subscribers' }],
          title: [{ value: userMailId }],
          field_subscribe_email: [{ value: userMailId }],
        },
        { headers: new HttpHeaders({ 'X-CSRF-Token': JSON.stringify(this.generateCRFSToken()) }) }
      )
      .toPromise()
      .then((data) => {
        return data;
      });
  }
  /**
   * Change password available after login if user wish to change.
   * Need to pass the old and new password also needs to pass the CRFS token, Authorization in headers
   * @param context
   * @param userProfile
   */
  changePassword(context: any, userProfile: any) {
    var id = userProfile.uid[0].value;
    //var href = environment.BaseURL + '/rest/type/user/user';
    var href = environment.BaseURL.replace('https://', 'http://') + '/rest/type/user/user';
    return this.http.patch(
      `${environment.BaseURL}/user/${id}?_format=hal_json`,
      {
        _links: {
          type: {
            href: href,
          },
        },
        pass: [{ existing: context.oldPassword, value: context.newpassword }],
        mail: { value: userProfile.mail[0].value },
      },
      {
        headers: {
          'X-CSRF-Token': this.getCRFSToken(),
          'Content-Type': 'application/hal+json',
          Authorization: 'Bearer ' + this.getToken(),
        },
      }
    );
  }
  /**
   * change the user name by passing user id and new username also by passing crfs,authorization in headers
   */
  changeUserName(context: any, userProfie: any) {
    var id = userProfie.uid[0].value;
    //var href = environment.BaseURL + '/rest/type/user/user';
    var href = environment.BaseURL.replace('https://', 'http://') + '/rest/type/user/user';
    let json = {
      _links: {
        type: {
          href: href,
        },
      },
    };
    if (context.newUserName && context.newUserName !== '' && context.newUserName !== null) {
      json['name'] = { value: context.newUserName };
    }
    if (context.field_country) {
      json['field_country'] = context.field_country;
    }
    return this.http.patch(`${environment.BaseURL}/user/${id}?_format=hal_json`, json, {
      headers: {
        'X-CSRF-Token': this.getCRFSToken(),
        'Content-Type': 'application/hal+json',
        Authorization: 'Bearer ' + this.getToken(),
      },
    });
  }
  /**
   * forgot password, will send the temporary password to the user if the given mail was registered
   * @param user
   */
  forgotPassword(user: any) {
    return this.http.post(`${environment.BaseURL}/${FORGOT_PASSWORD}`, {
      mail: user.email,
    });
  }
  /**
   * used to reset the password after forgot password clicked.
   * need to pass the new password and temporary password which was send to mail
   * @param context
   * @param currentUser
   */
  resetPassword(context: any, currentUser: any) {
    return this.http.post(`${environment.BaseURL}/${RESET_PASSWORD}`, {
      name: currentUser.source._value.current_user.name,
      temp_pass: context.tempPassword,
      new_pass: context.password,
    });
  }
  deleteuserProfile(userId: string) {
    return this.http.delete(`${environment.BaseURL}/user/${userId}?_format=hal_json`, {
      headers: {
        'X-CSRF-Token': this.getCRFSToken(),
        'Content-Type': 'application/hal+json',
        Authorization: 'Bearer ' + this.getToken(),
      },
    });
  }
  deleteAccount(body: any) {
    return this.http.post(`${environment.BaseURL}/api/user-delete`, body);
  }

  subscribeUser(data: any) {
    return this.http.post(`${environment.apiBaseURL}/subscribeuser`, data);
  }
}
